import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ControlContainer, FormGroupDirective } from '@angular/forms';

@Component({
  selector: 'app-currency-input',
  styleUrls: ['./currency-input.component.scss'],
  template: `
    <p-inputNumber
      [attr.data-cy]="dataCy"
      [attr.id]="id"
      [mode]="mode"
      [currency]="currency"
      [locale]="locale"
      [useGrouping]="true"
      [placeholder]="placeholder"
      [formControlName]="controlName"
      [minFractionDigits]="0"
      [maxFractionDigits]="0"
      [styleClass]="styleClass"
      [suffix]="suffix"
      [disabled]="disabled"
      [readonly]="readonly"
      (onInput)="handleInput($event)"
      (onBlur)="handleBlur($event)"
      (onFocus)="handleFocus($event)"
      (keydown)="handleKeyDown($event)">
    </p-inputNumber>
  `,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class CurrencyInputComponent {
  @Input() controlName: string;
  @Input() placeholder: string;
  @Input() dataCy?: string;
  @Input() id?: string;
  @Input() styleClass?: string;
  @Input() mode: string = 'currency';
  @Input() currency: string = 'PLN';
  @Input() locale: string = 'pl';
  @Input() suffix?: string;
  @Input() disabled?: boolean = false;
  @Input() readonly?: boolean = false;

  @Output() onBlur = new EventEmitter<any>();
  @Output() onFocus = new EventEmitter<any>();

  private lastKey: string | null = null;
  private lastCursorPosition: number | null = null;
  private isBackspacePressed = false;

  constructor(public parentForm: FormGroupDirective) {}

  handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.readonly) {
      return;
    }
    const input = event.target as HTMLInputElement;
    this.lastKey = event.key;
    this.lastCursorPosition = input.selectionStart;
    this.isBackspacePressed = event.key === 'Backspace';
  }

  handleBlur(event: any): void {
    this.onBlur.emit(event);
  }

  handleFocus(event: any): void {
    this.onFocus.emit(event);
  }

  handleInput(event: {
    originalEvent: InputEvent;
    value: number;
    formattedValue: string;
  }): void {
    if (this.disabled || this.readonly) {
      return;
    }
    const input = event.originalEvent.target as HTMLInputElement;

    setTimeout(() => {
      if (this.isBackspacePressed && this.lastCursorPosition !== null) {
        // For backspace, we need to find the previous digit position
        const value = input.value;
        let newPosition = this.lastCursorPosition;

        // Move back until we find a digit
        while (newPosition > 0) {
          newPosition--;
          // If we find a digit, position cursor after it
          if (/\d/.test(value[newPosition])) {
            newPosition++;
            break;
          }
        }

        input.setSelectionRange(newPosition, newPosition);
      } else if (this.lastKey && /^\d$/.test(this.lastKey)) {
        // For digit input, find the position of the last entered digit
        const digits = input.value.replace(/[^\d]/g, '');
        const lastDigitIndex = digits.lastIndexOf(this.lastKey);

        if (lastDigitIndex !== -1) {
          // Find the position of this digit in the formatted string
          let digitCount = 0;
          let newPosition = 0;

          for (
            let i = 0;
            i < input.value.length && digitCount <= lastDigitIndex;
            i++
          ) {
            if (/\d/.test(input.value[i])) {
              digitCount++;
            }
            newPosition = i + 1;
          }

          input.setSelectionRange(newPosition, newPosition);
        }
      }

      // Reset tracking variables
      this.lastKey = null;
      this.lastCursorPosition = null;
      this.isBackspacePressed = false;
    }, 0);
  }
}
